import React, { useCallback, useEffect, useMemo, useState } from 'react';
// react-bootstrap is not imported here — this surface uses the pat-* primitives.
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  AuditAggregates,
  AuditMatchedRule,
  AuditOverride,
  AuditOverrideField,
  AuditPreviewResponse,
  AuditRow,
  AuditSummary,
  ACTIVE_INGREDIENT_UNITS,
  DOSE_PER_100L_UNITS,
  Orchard,
  SpraySession,
  SprayProgramRef,
  sprayService,
} from '../../services/sprayService';
import PageHeader from '../shell/PageHeader';
import { DataTable, Field, FormSection, Modal, Notice, SaveBar, StatusPill } from '../patterns';
import type { DataTableColumn } from '../patterns';
import { formatDate } from '../../dateFormat';
import { formatMeasure } from '../../utils/formatMeasure';
import './AuditReportDashboard.css';

const todayIso = () => new Date().toISOString().slice(0, 10);
const ninetyDaysAgoIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
};

/**
 * The audit-report dashboard. Top section is a filter panel; the user
 * loads a preview which renders the rows table, the gap summary, and the
 * three aggregate metric tables. Gap rows can be patched inline via a
 * manual-override modal; once preview is loaded, "Export to Excel"
 * downloads the .xlsx with any applied overrides.
 *
 * Design notes:
 *   - Filters live in component state. The fetch only fires on the
 *     "Load Preview" click, so users can adjust multiple filters before
 *     paying the round-trip cost.
 *   - Overrides accumulate in `overrides` state keyed by
 *     `${sessionId}::${productId}` so each (session, product) row can
 *     have at most one override per field, but multiple fields per row.
 *   - The currently-rendered rows reflect overrides locally so the user
 *     sees the patched data before exporting; the backend re-applies the
 *     same overrides on export to produce the .xlsx.
 */
const AuditReportDashboard: React.FC = () => {
  const { t } = useTranslation();

  // Filter state
  const [startDate, setStartDate] = useState(ninetyDaysAgoIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [orchardIds, setOrchardIds] = useState<string[]>([]);
  const [sessionIds, setSessionIds] = useState<string[]>([]);

  // Reference-data lookups
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [completedSessions, setCompletedSessions] = useState<SpraySession[]>([]);

  // Preview state
  const [preview, setPreview] = useState<AuditPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Override state — keyed by `${sessionId}::${productId}`, value is field→override map.
  const [overrides, setOverrides] = useState<Record<string, Record<string, AuditOverride>>>({});
  const [overrideRow, setOverrideRow] = useState<AuditRow | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Load reference lookups once. Sessions are filtered to status=completed
  // because the audit only includes events that actually happened.
  useEffect(() => {
    sprayService.getOrchards()
      .then(setOrchards)
      .catch((err) => Sentry.captureException(err));
    sprayService.getSessions({ status: 'completed' })
      .then(setCompletedSessions)
      .catch((err) => Sentry.captureException(err));
  }, []);

  // Apply per-row overrides locally so the table reflects the patched
  // values before export. Aggregates are recomputed naively from the
  // patched activeIngredientKg values; we rely on the backend to produce
  // the authoritative aggregate set on export.
  const flatOverrides: AuditOverride[] = useMemo(() => {
    return Object.values(overrides).flatMap((perRow) => Object.values(perRow));
  }, [overrides]);

  const patchedRows: AuditRow[] = useMemo(() => {
    if (!preview) return [];
    return preview.rows.map((row) => {
      const key = `${row.sessionId}::${row.productId}`;
      const rowOverrides = overrides[key];
      if (!rowOverrides) return row;
      const patched: AuditRow = { ...row };
      for (const o of Object.values(rowOverrides)) {
        (patched as unknown as Record<string, unknown>)[o.field] = o.value;
      }
      // Drop the gap codes that the applied overrides directly address.
      // unit_mismatch / not_mass_convertible depend on the relationship
      // between two fields and we can't decide locally — those are
      // resolved by the backend at export time.
      const fixedFields = new Set(Object.values(rowOverrides).map((o) => o.field));
      const droppedGapCodes = new Set<string>();
      if (fixedFields.has('activeIngredient')) droppedGapCodes.add('missing_active_ingredient');
      if (fixedFields.has('activeIngredientValue')) droppedGapCodes.add('missing_active_ingredient_value');
      if (fixedFields.has('productUnit')) {
        droppedGapCodes.add('missing_unit');
        droppedGapCodes.add('invalid_unit');
      }
      if (fixedFields.has('productDosePer100L')) droppedGapCodes.add('missing_dose_per_100l');
      if (fixedFields.has('productDosePer100LUnit')) {
        droppedGapCodes.add('missing_quantity_unit');
        droppedGapCodes.add('invalid_quantity_unit');
      }
      if (fixedFields.has('orchardAreaHectares')) droppedGapCodes.add('missing_orchard_area_hectares');
      if (fixedFields.has('orchardVariety')) droppedGapCodes.add('missing_orchard_variety');
      // unit_mismatch can flip either way under overrides — drop the
      // original code, then re-add it if the patched pair is still
      // incompatible. Mirrors the backend's normaliseQuantity check.
      droppedGapCodes.add('unit_mismatch');
      let nextGaps = (row.gaps || []).filter((g) => !droppedGapCodes.has(g));
      if (
        patched.productUnit &&
        patched.quantityUnit &&
        !isUnitPairCompatible(patched.productUnit, patched.quantityUnit)
      ) {
        nextGaps = [...nextGaps, 'unit_mismatch'];
      }
      patched.gaps = nextGaps;
      // Tag visually so the user can see overridden rows.
      (patched as AuditRow & { _hasOverride?: boolean })._hasOverride = true;
      return patched;
    });
  }, [preview, overrides]);

  const validateFilters = useCallback(() => {
    if (!startDate || !endDate) {
      setFilterError(t('spray.auditReport.filters.missingDates'));
      return false;
    }
    if (endDate < startDate) {
      setFilterError(t('spray.auditReport.filters.endBeforeStart'));
      return false;
    }
    setFilterError(null);
    return true;
  }, [startDate, endDate, t]);

  const handleLoadPreview = useCallback(async () => {
    if (!validateFilters()) return;
    setLoading(true);
    setLoadError(null);
    setExportSuccess(false);
    setExportError(null);
    try {
      const data = await sprayService.getAuditReportPreview({
        startDate, endDate, orchardIds, sessionIds,
      });
      setPreview(data);
      // Drop overrides whose target row is no longer in the preview.
      setOverrides((prev) => {
        const next: typeof prev = {};
        for (const row of data.rows) {
          const key = `${row.sessionId}::${row.productId}`;
          if (prev[key]) next[key] = prev[key];
        }
        return next;
      });
    } catch (err) {
      Sentry.captureException(err);
      setLoadError(t('spray.auditReport.summary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, orchardIds, sessionIds, validateFilters, t]);

  // Apply ALL of the user's entered values from the tabular modal in one
  // shot. Each non-empty value becomes an override entry keyed by
  // (sessionId, productId, field).
  const handleApplyOverrides = useCallback(
    (row: AuditRow, entries: Array<{ field: AuditOverrideField; value: string | number | null }>) => {
      if (entries.length === 0) {
        setOverrideRow(null);
        return;
      }
      const key = `${row.sessionId}::${row.productId}`;
      setOverrides((prev) => {
        const rowOverrides: Record<string, AuditOverride> = { ...(prev[key] || {}) };
        for (const e of entries) {
          rowOverrides[e.field] = {
            sessionId: row.sessionId,
            productId: row.productId || '',
            field: e.field,
            value: e.value,
          };
        }
        return { ...prev, [key]: rowOverrides };
      });
      setOverrideRow(null);
    },
    []
  );

  const handleExport = useCallback(async () => {
    if (!preview) return;
    setExporting(true);
    setExportError(null);
    setExportSuccess(false);
    try {
      const blob = await sprayService.exportAuditReport(
        { startDate, endDate, orchardIds, sessionIds },
        flatOverrides
      );
      // Trigger browser download via a transient anchor.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t('spray.auditReport.export.downloadFilename');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportSuccess(true);
    } catch (err) {
      Sentry.captureException(err);
      setExportError(t('spray.auditReport.export.errorToast'));
    } finally {
      setExporting(false);
    }
  }, [preview, startDate, endDate, orchardIds, sessionIds, flatOverrides, t]);

  return (
    <div className="audit-report-dashboard" data-testid="audit-report-dashboard">
      <PageHeader
        title={t('spray.auditReport.title')}
        meta={t('spray.auditReport.intro')}
      />
      <div className="audit-report-dashboard__body">
        <FilterPanel
          startDate={startDate}      onStartDateChange={setStartDate}
          endDate={endDate}          onEndDateChange={setEndDate}
          orchards={orchards}        orchardIds={orchardIds}     onOrchardIdsChange={setOrchardIds}
          sessions={completedSessions} sessionIds={sessionIds}   onSessionIdsChange={setSessionIds}
          onLoad={handleLoadPreview} loading={loading}
          filterError={filterError}
        />

        {loadError && (
          <Notice tone="danger" testId="audit-load-error">
            {loadError}
          </Notice>
        )}

        {preview && (
          <>
            <SummaryCard summary={preview.summary} loading={loading} />
            <PreviewTable
              rows={patchedRows}
              onRequestOverride={setOverrideRow}
            />
            <AggregatesPanel aggregates={preview.aggregates} />
            <ExportBar
              onExport={handleExport}
              exporting={exporting}
              success={exportSuccess}
              error={exportError}
            />
          </>
        )}

        <OverrideModal
          row={overrideRow}
          onCancel={() => setOverrideRow(null)}
          onSave={handleApplyOverrides}
        />
      </div>
    </div>
  );
};

// ─── Filter panel ─────────────────────────────────────────────────────────────

interface FilterPanelProps {
  startDate: string; onStartDateChange: (v: string) => void;
  endDate: string;   onEndDateChange:   (v: string) => void;
  orchards: Orchard[]; orchardIds: string[]; onOrchardIdsChange: (v: string[]) => void;
  sessions: SpraySession[]; sessionIds: string[]; onSessionIdsChange: (v: string[]) => void;
  onLoad: () => void; loading: boolean;
  filterError: string | null;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  startDate, onStartDateChange, endDate, onEndDateChange,
  orchards, orchardIds, onOrchardIdsChange,
  sessions, sessionIds, onSessionIdsChange,
  onLoad, loading, filterError,
}) => {
  const { t } = useTranslation();
  const handleMulti = (e: React.ChangeEvent<HTMLSelectElement>, setter: (v: string[]) => void) => {
    const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setter(selected);
  };

  // Build a lookup of orchardId → orchardName for the session option label.
  const orchardNameById = new Map(orchards.map((o) => [o._id, o.orchardName]));

  const sessionLabel = (s: SpraySession): string => {
    // Display label (rendered in the session-picker option) — sessions carry
    // date-only calendar dates, so format in UTC to preserve the stored day
    // regardless of the viewer's local timezone.
    const date = s.date ? formatDate(s.date, { timeZone: 'UTC' }) : '—';
    const orchardName = (s.orchardIds && s.orchardIds[0])
      ? orchardNameById.get(s.orchardIds[0]) || '—'
      : '—';
    const programName = typeof s.programId === 'object' && s.programId
      ? (s.programId as SprayProgramRef).name
      : '';
    const trailing = s.name || programName || '';
    return `${date} — ${orchardName}${trailing ? ' — ' + trailing : ''}`;
  };

  return (
    <div data-testid="audit-filter-panel">
      <FormSection
        id="audit-filter"
        title={t('spray.auditReport.filters.heading')}
        description={t('spray.auditReport.filters.sectionDescription')}
      >
        <div className="audit-filter-grid">
          <Field
            label={t('spray.auditReport.filters.startDate')}
            controlId="audit-filter-start-date"
          >
            <input
              id="audit-filter-start-date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              data-testid="filter-start-date"
            />
          </Field>
          <Field
            label={t('spray.auditReport.filters.endDate')}
            controlId="audit-filter-end-date"
          >
            <input
              id="audit-filter-end-date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              data-testid="filter-end-date"
            />
          </Field>
          <Field
            label={t('spray.auditReport.filters.orchards')}
            controlId="audit-filter-orchards"
          >
            <select
              id="audit-filter-orchards"
              multiple
              value={orchardIds}
              onChange={(e) => handleMulti(e, onOrchardIdsChange)}
              data-testid="filter-orchards"
              size={4}
            >
              {orchards.map((o) => (
                <option key={o._id} value={o._id}>
                  {o.orchardName}{o.variety ? ` — ${o.variety}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={t('spray.auditReport.filters.sessions')}
            controlId="audit-filter-sessions"
          >
            <select
              id="audit-filter-sessions"
              multiple
              value={sessionIds}
              onChange={(e) => handleMulti(e, onSessionIdsChange)}
              data-testid="filter-sessions"
              size={4}
            >
              {sessions.map((s) => (
                <option key={s._id} value={s._id}>{sessionLabel(s)}</option>
              ))}
            </select>
          </Field>
        </div>
        {filterError && (
          <div role="alert" className="audit-filter-error" data-testid="filter-error">
            {filterError}
          </div>
        )}
        <div className="audit-filter-actions">
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={onLoad}
            disabled={loading}
            data-testid="load-preview-btn"
          >
            {loading
              ? t('spray.auditReport.filters.loading')
              : t('spray.auditReport.filters.loadPreview')}
          </button>
        </div>
      </FormSection>
    </div>
  );
};

// ─── Summary ──────────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ summary: AuditSummary; loading: boolean }> = ({ summary }) => {
  const { t } = useTranslation();
  if (summary.totalRows === 0) {
    return (
      <div role="alert" className="audit-info-alert" data-testid="audit-summary-empty">
        {t('spray.auditReport.summary.noResults')}
      </div>
    );
  }
  // The on-screen summary intentionally shows only the row + gap counts.
  // The per-active-ingredient breakdown is rendered in the Excel export
  // (Summary tab → "Total Active Ingredient per AI") rather than the
  // preview UI, to keep the dashboard scannable on first load.
  return (
    <div data-testid="audit-summary">
      <FormSection
        id="audit-summary"
        title={t('spray.auditReport.summary.heading')}
      >
        <div className="audit-summary-grid">
          <div className="audit-summary-stat">
            <div className="audit-summary-stat__label">
              {t('spray.auditReport.summary.totalRows')}
            </div>
            <div className="audit-summary-stat__value" data-testid="summary-rows">
              {summary.totalRows}
            </div>
          </div>
          <div className="audit-summary-stat">
            <div className="audit-summary-stat__label">
              {t('spray.auditReport.summary.totalGaps')}
            </div>
            <div className="audit-summary-stat__value" data-testid="summary-gaps">
              <StatusPill variant={summary.totalGaps > 0 ? 'warn' : 'ok'}>
                {summary.totalGaps}
              </StatusPill>
            </div>
          </div>
        </div>
      </FormSection>
    </div>
  );
};

// ─── Rows table ───────────────────────────────────────────────────────────────

interface PreviewTableProps {
  rows: AuditRow[];
  onRequestOverride: (row: AuditRow) => void;
}

type IndexedAuditRow = AuditRow & { _idx: number };

const PreviewTable: React.FC<PreviewTableProps> = ({ rows, onRequestOverride }) => {
  const { t } = useTranslation();
  if (rows.length === 0) return null;

  const indexedRows: IndexedAuditRow[] = rows.map((row, idx) => ({ ...row, _idx: idx }));

  const columns: DataTableColumn<IndexedAuditRow>[] = [
    {
      key: 'date',
      label: t('spray.auditReport.table.columns.date'),
      render: (row) => (row.sessionDate ? formatDate(row.sessionDate, { timeZone: 'UTC' }) : '—'),
    },
    {
      key: 'orchard',
      label: t('spray.auditReport.table.columns.orchard'),
      render: (row) => row.orchardName ?? '—',
    },
    {
      key: 'variety',
      label: t('spray.auditReport.table.columns.variety'),
      render: (row) => row.orchardVariety ?? '—',
    },
    {
      key: 'product',
      label: t('spray.auditReport.table.columns.product'),
      render: (row) => row.productName ?? '—',
    },
    {
      key: 'lNumber',
      label: t('spray.auditReport.table.columns.lNumber'),
      render: (row) => row.lNumber ?? '—',
    },
    {
      key: 'activeIngredient',
      label: t('spray.auditReport.table.columns.activeIngredient'),
      render: (row) => formatAiCell(row),
    },
    {
      key: 'quantity',
      label: t('spray.auditReport.table.columns.quantity'),
      render: (row) => formatQuantityCell(row),
    },
    {
      key: 'aiTotal',
      label: t('spray.auditReport.table.columns.aiTotal'),
      render: (row) => renderAiTotalCell(row, row._idx, t),
    },
    {
      key: 'aiUnit',
      label: t('spray.auditReport.table.columns.aiUnit'),
      render: (row) => renderAiUnitCell(row),
    },
    {
      key: 'gaps',
      label: t('spray.auditReport.table.columns.gaps'),
      render: (row) => (
        row.gaps && row.gaps.length > 0
          ? (
              <span className="audit-preview__gap-list">
                {row.gaps.map((g) => (
                  <StatusPill key={g} variant="warn">
                    {t(`spray.auditReport.gaps.${g}`, { defaultValue: g })}
                  </StatusPill>
                ))}
              </span>
            )
          : null
      ),
    },
    {
      key: 'actions',
      label: t('spray.auditReport.table.columns.actions'),
      render: (row) => (
        row.gaps && row.gaps.length > 0
          ? (
              <button
                type="button"
                className="pat-btn pat-btn--outline"
                onClick={() => onRequestOverride(row)}
                data-testid={`fix-gap-btn-${row._idx}`}
              >
                {t('spray.auditReport.table.fixGap')}
              </button>
            )
          : null
      ),
    },
    {
      key: 'ruleReasoning',
      label: '',
      render: (row) => (
        <RuleReasoningDisclosure row={row} />
      ),
    },
  ];

  return (
    <FormSection
      id="audit-preview"
      title={t('spray.auditReport.table.heading')}
    >
      <div data-testid="audit-preview-table">
        <DataTable<IndexedAuditRow>
          columns={columns}
          rows={indexedRows}
          rowKey={(row) => `${row.sessionId}::${row.productId}::${row._idx}`}
          rowClassName={(row) => (row.gaps && row.gaps.length > 0) ? 'table-warning' : ''}
        />
      </div>
    </FormSection>
  );
};

/**
 * Local mirror of the backend's normaliseQuantity compatibility check.
 * Returns true when the productUnit and quantityUnit can be combined to
 * produce a kg figure (g/L with ml/L; g/kg with g/kg; spores/ml with
 * ml/L). Anything else is a mismatch.
 *
 * Why duplicate the rule: the override modal needs immediate feedback
 * when the user picks an incompatible pair, and the patched row needs to
 * re-flag unit_mismatch if an override creates one. The check is five
 * lines and stable; the alternative is a round-trip per override.
 */
function isUnitPairCompatible(productUnit: string, quantityUnit: string): boolean {
  // Case-insensitive on the g/kg branch only: legacy rows persist the old
  // `g/Kg` casing until lazily re-saved (no backfill migration), so this read
  // path must accept both. g/L and spores/ml never had a casing variant.
  if (productUnit === 'g/L') return quantityUnit === 'L' || quantityUnit === 'ml';
  if (String(productUnit).toLowerCase() === 'g/kg') return quantityUnit === 'kg' || quantityUnit === 'g';
  if (productUnit === 'spores/ml') return quantityUnit === 'ml' || quantityUnit === 'L';
  return false;
}

/**
 * Map a row's current `gaps` array onto the set of fields the user can
 * fix from the override modal. Gaps that represent "entity not found"
 * (missing_orchard, missing_product) and biologic-spore non-convertibility
 * have no fixable field and are excluded from the modal — see the
 * gap-closure guidance for the structural fix.
 */
function getFixableFields(gaps: string[]): AuditOverrideField[] {
  const fields = new Set<AuditOverrideField>();
  for (const g of gaps) {
    switch (g) {
      case 'missing_active_ingredient':       fields.add('activeIngredient'); break;
      case 'missing_active_ingredient_value': fields.add('activeIngredientValue'); break;
      case 'missing_unit':
      case 'invalid_unit':                    fields.add('productUnit'); break;
      case 'missing_dose_per_100l':           fields.add('productDosePer100L'); break;
      case 'missing_quantity_unit':
      case 'invalid_quantity_unit':           fields.add('productDosePer100LUnit'); break;
      case 'unit_mismatch':
        fields.add('productUnit');
        fields.add('productDosePer100LUnit');
        break;
      case 'missing_orchard_area_hectares':   fields.add('orchardAreaHectares'); break;
      case 'missing_orchard_variety':         fields.add('orchardVariety'); break;
      // missing_quantity is on the consumption row, not overridable —
      // see gap-closure guidance.
      // missing_orchard / missing_product / not_mass_convertible — see above.
      default: break;
    }
  }
  // Stable, predictable order matching the audit row layout.
  const order: AuditOverrideField[] = [
    'activeIngredient',
    'activeIngredientValue',
    'productUnit',
    'productDosePer100L',
    'productDosePer100LUnit',
    'orchardAreaHectares',
    'orchardVariety',
  ];
  return order.filter((f) => fields.has(f));
}

/**
 * Render the "Active Ingredient" cell. Show partial values when only one
 * of (value, unit) is known so the user can see their override take
 * effect even if the paired field hasn't been filled in yet.
 */
function formatAiCell(row: AuditRow): string {
  if (row.activeIngredientValue == null && !row.productUnit) return '—';
  const value = row.activeIngredientValue != null ? String(row.activeIngredientValue) : '?';
  const unit = row.productUnit || '?';
  return `${value} ${unit}`;
}

/**
 * Render the "Quantity" cell — the consumed quantity (a plain mass/volume,
 * unlike the AI cell's concentration unit). When both value and unit are
 * present it is rescaled to a readable unit via formatMeasure (18000 ml ->
 * 18 L); partial rows keep the '?' sentinel so a gap stays visible.
 */
function formatQuantityCell(row: AuditRow): string {
  if (row.quantityUsed == null && !row.quantityUnit) return '—';
  if (row.quantityUsed != null && row.quantityUnit) {
    return formatMeasure(row.quantityUsed, row.quantityUnit);
  }
  const value = row.quantityUsed != null ? String(row.quantityUsed) : '?';
  const unit = row.quantityUnit || '?';
  return `${value} ${unit}`;
}

/**
 * Render the "AI Total" cell. For non-biologic products this is the
 * activeIngredientKg figure. For biologic (`spores/ml`) products it's
 * the activeIngredientAmount in spores. When the row has overrides
 * applied but the kg can't be recomputed locally, show a "(will
 * calculate on export)" placeholder so users know the export will
 * resolve it.
 */
function renderAiTotalCell(
  row: AuditRow,
  idx: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
  if (row.activeIngredientKg != null) {
    return row.activeIngredientKg.toFixed(4);
  }
  if (
    row.activeIngredientAmount != null &&
    row.activeIngredientUnit === 'spores'
  ) {
    return row.activeIngredientAmount.toLocaleString();
  }
  if ((row as AuditRow & { _hasOverride?: boolean })._hasOverride) {
    return (
      <span className="text-muted fst-italic" data-testid={`kg-pending-${idx}`}>
        {t('spray.auditReport.table.kgPending')}
      </span>
    );
  }
  return t('spray.auditReport.table.noKg');
}

/**
 * Render the "AI Unit" cell. Reports the natural unit of the AI total:
 * 'kg' for non-biologic mass-comparable products, 'spores' for
 * biologics, or a dash placeholder when the total isn't known.
 */
function renderAiUnitCell(row: AuditRow): string {
  if (row.activeIngredientKg != null) return 'kg';
  if (
    row.activeIngredientAmount != null &&
    row.activeIngredientUnit === 'spores'
  ) {
    return 'spores';
  }
  return '—';
}

// ─── Rule-reasoning disclosure ────────────────────────────────────────────────

/**
 * Renders an expandable <details> element showing which product rules were
 * matched for this audit row, and which rule supplied the chosen max dose
 * and max PHI.
 *
 * Only rendered when the row has at least one matched rule. Legacy
 * pre-snapshot rows additionally receive a "pre-redesign" badge.
 */
const RuleReasoningDisclosure: React.FC<{ row: AuditRow }> = ({ row }) => {
  const { t } = useTranslation();
  const hasRules = row.matchedRules && row.matchedRules.length > 0;

  return (
    <>
      {hasRules && (
        <details data-testid={`audit-row-${row.sessionId}-rule-reasoning`}>
          <summary>
            {t('spray.auditReport.ruleReasoning.summary', {
              resolvedFrom: row.resolvedFrom ?? '',
              defaultValue: `Rule reasoning (${row.resolvedFrom ?? ''})`,
            })}
          </summary>
          <table className="table table-sm audit-rule-reasoning__table">
            <thead>
              <tr>
                <th>{t('spray.auditReport.ruleReasoning.headerCrops', { defaultValue: 'Crops' })}</th>
                <th>{t('spray.auditReport.ruleReasoning.headerPests', { defaultValue: 'Pests' })}</th>
                <th>{t('spray.auditReport.ruleReasoning.headerDose', { defaultValue: 'Dose' })}</th>
                <th>{t('spray.auditReport.ruleReasoning.headerPhi', { defaultValue: 'PHI' })}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(row.matchedRules as AuditMatchedRule[]).map((mr, i) => (
                <tr key={i}>
                  <td>{mr.crops?.join(', ') || '—'}</td>
                  <td>
                    {mr.pests && mr.pests.length > 0
                      ? mr.pests.join(', ')
                      : t('spray.auditReport.ruleReasoning.anyPest', { defaultValue: 'any' })}
                  </td>
                  <td>
                    {mr.dose
                      ? `${mr.dose.value} ${mr.dose.unit}/${mr.dose.denominator}`
                      : '—'}
                  </td>
                  <td>
                    {mr.phi ? `${mr.phi.value} ${mr.phi.unit}` : '—'}
                  </td>
                  <td>
                    {String(mr.ruleId) === String(row.doseSourceRuleId) && (
                      <span
                        className="badge bg-info ms-2"
                        data-testid="max-dose-badge"
                      >
                        {t('spray.auditReport.ruleReasoning.maxDose', { defaultValue: 'max dose' })}
                      </span>
                    )}
                    {String(mr.ruleId) === String(row.phiSourceRuleId) && (
                      <span
                        className="badge bg-info ms-2"
                        data-testid="max-phi-badge"
                      >
                        {t('spray.auditReport.ruleReasoning.maxPhi', { defaultValue: 'max PHI' })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {row.legacyPreSnapshot && (
        <span
          className="badge bg-warning text-dark"
          data-testid={`audit-row-${row.sessionId}-legacy-badge`}
        >
          {t('spray.auditReport.ruleReasoning.legacyBadge', { defaultValue: 'pre-redesign' })}
        </span>
      )}
    </>
  );
};

// ─── Aggregates ───────────────────────────────────────────────────────────────

const AggregatesPanel: React.FC<{ aggregates: AuditAggregates }> = ({ aggregates }) => {
  const { t } = useTranslation();
  const empty =
    aggregates.kgPerCrop.length === 0 &&
    aggregates.kgPerMonth.length === 0 &&
    aggregates.kgPerHaPerMonth.length === 0;
  if (empty) return null;

  const fmt = (amount: number, unit: string) =>
    unit === 'spores' ? amount.toLocaleString() : amount.toFixed(3);

  return (
    <div data-testid="audit-aggregates">
      <FormSection
        id="audit-aggregates"
        title={t('spray.auditReport.aggregates.heading')}
      >
        <h4 className="audit-aggregates__subheading">
          {t('spray.auditReport.aggregates.kgPerCrop')}
        </h4>
        <table className="audit-aggregates-table" data-testid="agg-kg-per-crop">
          <thead>
            <tr>
              <th>{t('spray.auditReport.aggregates.variety')}</th>
              <th>{t('spray.auditReport.aggregates.activeIngredient')}</th>
              <th className="audit-aggregates-table__num">{t('spray.auditReport.aggregates.amount')}</th>
              <th>{t('spray.auditReport.aggregates.unit')}</th>
            </tr>
          </thead>
          <tbody>
            {aggregates.kgPerCrop.map((b) => (
              <tr key={`crop-${b.variety ?? 'null'}-${b.activeIngredient}-${b.unit}`}>
                <td>{b.variety ?? t('spray.auditReport.aggregates.unknownVariety')}</td>
                <td>{b.activeIngredient}</td>
                <td className="audit-aggregates-table__num">{fmt(b.amount, b.unit)}</td>
                <td>{b.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h4 className="audit-aggregates__subheading">
          {t('spray.auditReport.aggregates.kgPerMonth')}
        </h4>
        <table className="audit-aggregates-table" data-testid="agg-kg-per-month">
          <thead>
            <tr>
              <th>{t('spray.auditReport.aggregates.month')}</th>
              <th>{t('spray.auditReport.aggregates.activeIngredient')}</th>
              <th className="audit-aggregates-table__num">{t('spray.auditReport.aggregates.amount')}</th>
              <th>{t('spray.auditReport.aggregates.unit')}</th>
            </tr>
          </thead>
          <tbody>
            {aggregates.kgPerMonth.map((b) => (
              <tr key={`month-${b.month}-${b.activeIngredient}-${b.unit}`}>
                <td>{b.month}</td>
                <td>{b.activeIngredient}</td>
                <td className="audit-aggregates-table__num">{fmt(b.amount, b.unit)}</td>
                <td>{b.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h4 className="audit-aggregates__subheading">
          {t('spray.auditReport.aggregates.kgPerHaPerMonth')}
        </h4>
        <table className="audit-aggregates-table" data-testid="agg-kg-per-ha-per-month">
          <thead>
            <tr>
              <th>{t('spray.auditReport.aggregates.variety')}</th>
              <th>{t('spray.auditReport.aggregates.month')}</th>
              <th>{t('spray.auditReport.aggregates.activeIngredient')}</th>
              <th className="audit-aggregates-table__num">{t('spray.auditReport.aggregates.amount')}</th>
              <th>{t('spray.auditReport.aggregates.unit')}</th>
              <th className="audit-aggregates-table__num">{t('spray.auditReport.aggregates.hectares')}</th>
              <th className="audit-aggregates-table__num">{t('spray.auditReport.aggregates.amountPerHa')}</th>
            </tr>
          </thead>
          <tbody>
            {aggregates.kgPerHaPerMonth.map((b) => (
              <tr key={`cm-${b.variety ?? 'null'}-${b.month}-${b.activeIngredient}-${b.unit}`}>
                <td>{b.variety ?? t('spray.auditReport.aggregates.unknownVariety')}</td>
                <td>{b.month}</td>
                <td>{b.activeIngredient}</td>
                <td className="audit-aggregates-table__num">{fmt(b.amount, b.unit)}</td>
                <td>{b.unit}</td>
                <td className="audit-aggregates-table__num">{b.hectares.toFixed(2)}</td>
                <td className="audit-aggregates-table__num">{b.amountPerHa != null ? fmt(b.amountPerHa, b.unit) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </FormSection>
    </div>
  );
};

// ─── Export bar ───────────────────────────────────────────────────────────────

interface ExportBarProps {
  onExport: () => void;
  exporting: boolean;
  success: boolean;
  error: string | null;
}

const ExportBar: React.FC<ExportBarProps> = ({ onExport, exporting, success, error }) => {
  const { t } = useTranslation();
  const status = error
    ? <span className="audit-export-status audit-export-status--danger" role="alert">{error}</span>
    : success
      ? <span className="audit-export-status audit-export-status--success">{t('spray.auditReport.export.successToast')}</span>
      : null;
  return (
    <div data-testid="audit-export-bar">
      <SaveBar
        status={status}
        primaryAction={{
          label: exporting
            ? t('spray.auditReport.export.exporting')
            : t('spray.auditReport.export.button'),
          onClick: onExport,
          disabled: exporting,
        }}
      />
    </div>
  );
};

// ─── Override modal (tabular) ─────────────────────────────────────────────────

interface OverrideModalProps {
  row: AuditRow | null;
  onCancel: () => void;
  onSave: (
    row: AuditRow,
    entries: Array<{ field: AuditOverrideField; value: string | number | null }>
  ) => void;
}

/**
 * Inputs are kept as strings while editing (HTML form inputs always
 * surface strings). Coercion to number / null happens at Save time.
 */
type OverrideInputs = Partial<Record<AuditOverrideField, string>>;

const NUMBER_FIELDS = new Set<AuditOverrideField>([
  'activeIngredientValue',
  'orchardAreaHectares',
  'productDosePer100L',
]);

const OverrideModal: React.FC<OverrideModalProps> = ({ row, onCancel, onSave }) => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState<OverrideInputs>({});

  // The set of fields rendered in the modal is derived from the row's
  // current gaps. Resets whenever a different row opens the modal.
  const fixableFields = useMemo(
    () => (row ? getFixableFields(row.gaps || []) : []),
    [row]
  );

  useEffect(() => {
    if (row) {
      // Pre-populate with the row's current value for each fixable
      // field so the user can edit (e.g. for unit_mismatch the existing
      // pair is shown so they can change one).
      const seed: OverrideInputs = {};
      for (const f of getFixableFields(row.gaps || [])) {
        const current = (row as unknown as Record<string, unknown>)[f];
        seed[f] = current == null ? '' : String(current);
      }
      setInputs(seed);
    }
  }, [row]);

  if (!row) return null;

  // Live unit-mismatch detection: if both unit fields have values, flag a
  // mismatch as the user types so they can correct before saving.
  const aiUnit = inputs.productUnit ?? row.productUnit ?? '';
  const doseUnit = inputs.productDosePer100LUnit ?? row.quantityUnit ?? '';
  const showMismatchWarning =
    !!aiUnit && !!doseUnit && !isUnitPairCompatible(aiUnit, doseUnit);

  const setField = (f: AuditOverrideField, v: string) => {
    setInputs((prev) => ({ ...prev, [f]: v }));
  };

  const handleSave = () => {
    const entries: Array<{ field: AuditOverrideField; value: string | number | null }> = [];
    for (const f of fixableFields) {
      const raw = inputs[f];
      if (raw == null) continue;
      const trimmed = raw.trim();
      let value: string | number | null = trimmed === '' ? null : trimmed;
      if (value != null && NUMBER_FIELDS.has(f)) {
        const n = Number(value);
        value = Number.isFinite(n) ? n : null;
      }
      // Skip entries the user left blank AND the row doesn't already have
      // a populated value (no point saving null over null). When the row
      // had a value we DO write the new value, including blanking it out
      // — useful for clearing an incompatible unit before re-picking.
      const rowValue = (row as unknown as Record<string, unknown>)[f];
      if (value == null && rowValue == null) continue;
      entries.push({ field: f, value });
    }
    onSave(row, entries);
  };

  return (
    <Modal show onHide={onCancel} title={t('spray.auditReport.override.modalTitle')} size="lg">
      <Modal.Body>
        <div data-testid="override-modal">
          <p className="audit-override__intro">{t('spray.auditReport.override.modalIntro')}</p>
          <p className="audit-override__context">
            {t('spray.auditReport.override.rowContext', {
              product: row.productName ?? '—',
              orchard: row.orchardName ?? '—',
              date: row.sessionDate ? formatDate(row.sessionDate, { timeZone: 'UTC' }) : '—',
            })}
          </p>

          {fixableFields.length === 0 ? (
            <Notice tone="info" role="alert" testId="override-no-fixable">
              {t('spray.auditReport.override.noFixable')}
            </Notice>
          ) : (
            <>
              <table className="audit-override-table" data-testid="override-table">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>{t('spray.auditReport.override.field')}</th>
                    <th style={{ width: '60%' }}>{t('spray.auditReport.override.value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fixableFields.map((f) => (
                    <OverrideRow
                      key={f}
                      field={f}
                      inputValue={inputs[f] ?? ''}
                      onChange={(v) => setField(f, v)}
                      rowHasCurrentValue={
                        (row as unknown as Record<string, unknown>)[f] != null &&
                        (row as unknown as Record<string, unknown>)[f] !== ''
                      }
                    />
                  ))}
                </tbody>
              </table>

              {showMismatchWarning && (
                <Notice tone="warn" testId="override-mismatch-warning">
                  {t('spray.auditReport.override.unitMismatchWarning', {
                    aiUnit,
                    doseUnit,
                  })}
                </Notice>
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={onCancel}
          data-testid="override-cancel"
        >
          {t('spray.auditReport.override.cancel')}
        </button>
        {fixableFields.length > 0 && (
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={handleSave}
            data-testid="override-save"
          >
            {t('spray.auditReport.override.save')}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

interface OverrideRowProps {
  field: AuditOverrideField;
  inputValue: string;
  onChange: (v: string) => void;
  rowHasCurrentValue: boolean;
}

const OverrideRow: React.FC<OverrideRowProps> = ({ field, inputValue, onChange, rowHasCurrentValue }) => {
  const { t } = useTranslation();
  const inputType =
    NUMBER_FIELDS.has(field) ? 'number' : 'text';

  // Two unit fields render as enum dropdowns; everything else is a text/number input.
  const renderInput = () => {
    if (field === 'productUnit') {
      return (
        <select
          className="audit-override-input"
          value={inputValue}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`override-input-${field}`}
        >
          <option value="">{rowHasCurrentValue ? '' : t('spray.auditReport.override.currentMissing')}</option>
          {ACTIVE_INGREDIENT_UNITS.map((u) => (<option key={u} value={u}>{u}</option>))}
        </select>
      );
    }
    if (field === 'productDosePer100LUnit') {
      return (
        <select
          className="audit-override-input"
          value={inputValue}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`override-input-${field}`}
        >
          <option value="">{rowHasCurrentValue ? '' : t('spray.auditReport.override.currentMissing')}</option>
          {DOSE_PER_100L_UNITS.map((u) => (<option key={u} value={u}>{u}</option>))}
        </select>
      );
    }
    return (
      <input
        className="audit-override-input"
        type={inputType}
        value={inputValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={rowHasCurrentValue ? '' : t('spray.auditReport.override.currentMissing')}
        data-testid={`override-input-${field}`}
      />
    );
  };

  return (
    <tr>
      <td>{t(`spray.auditReport.override.fields.${field}`)}</td>
      <td>{renderInput()}</td>
    </tr>
  );
};

export default AuditReportDashboard;
